import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/services/queryClient'
import { endpoints } from '@/services/endpoints'
import { createSharedSseConnection, sseEnabled } from '@/services/sse'
import { usePermission } from '@/features/auth/permissions'
import { createLogbookEntry, fetchLogbook, fetchLogbookEntry, toEntry, updateLogbookEntry } from './api'
import { subscribeToLogbookList } from './liveStream'

/**
 * The log. LIVE.
 *
 * ── Two permissions, and either one opens the screen ─────────────────────
 * Every logbook route accepts `logbook.manage` OR `logbook.record_entries`, so
 * holding just the second is enough to read the log as well as write to it.
 * This hook answers the question once and both the page and the dialog gate on
 * it, rather than each spelling out the pair.
 *
 * `is_root` bypasses, as it does server-side — see `usePermission`.
 */
export function useLogbookAccess() {
  const manage = usePermission('logbook.manage')
  const record = usePermission('logbook.record_entries')
  return {
    isPending: manage.isPending || record.isPending,
    granted: manage.granted || record.granted,
    /* Changing somebody else's observation is the one act the two permissions
       do not share: `authorize_logbook_entry_write` lets `logbook.record_entries`
       change only entries its holder created, and nothing on the payload says
       who wrote a row — so this client cannot tell whose is whose.

       Edit is offered to every reader anyway, and a 403 is said in words on
       the toast: a missing button on half the feed reads as "edit does not
       exist", which is worse than a refusal that explains itself. Delete is
       still not offered — it cannot be undone, and guessing at it is not the
       same trade. Recorded in BACKEND-GAPS.md §9. */
    canManageAny: manage.granted,
  }
}

/**
 * One page of the log.
 *
 * `placeholderData` keeps the feed on screen while a filter lands. Blanking a
 * column of prose to a skeleton on every chip is a page that jumps under the
 * reader — and the counts above it would flash too, since they ride the same
 * response.
 *
 * Five minutes stale. A logbook is written by hand a few times a shift;
 * nothing in it changes because the clock moved, so nothing here polls —
 * liveness comes from `useLogbookListStream`.
 */
export const useLogbook = (params, { enabled = true } = {}) =>
  useQuery({
    queryKey: queryKeys.logbook(params),
    queryFn: ({ signal }) => fetchLogbook(params, signal),
    placeholderData: (previous) => previous,
    enabled,
    staleTime: 5 * 60_000,
  })

/**
 * One entry, in full — the observation, the photos, the signal windows and the
 * linked briefs the list row does not carry.
 *
 * `gcTime: 0` because `useLogbookEntryStream` writes into this key and lives
 * exactly as long as this hook's observer; a cached copy outliving it would be
 * served stale, with photo URLs past their presigned life, on the next open.
 */
export const useLogbookEntry = (id, { enabled = true } = {}) =>
  useQuery({
    queryKey: queryKeys.logbookEntry(id),
    queryFn: ({ signal }) => fetchLogbookEntry(id, signal),
    enabled: enabled && Boolean(id),
    staleTime: 0,
    gcTime: 0,
  })

/**
 * Keep the log live — `GET /logbook/stream`. Opt-in: mounting this is what
 * makes a list live. Each event refetches every list page that is mounted.
 *
 * @returns {{status: 'idle'|'connecting'|'live'|'retrying'|'failed'}}
 */
export function useLogbookListStream({ enabled = true } = {}) {
  const client = useQueryClient()
  const [status, setStatus] = useState('idle')

  useEffect(() => {
    if (!enabled || !sseEnabled()) return undefined
    return subscribeToLogbookList(
      () => client.invalidateQueries({ queryKey: ['logbook', 'list'] }),
      setStatus,
    )
  }, [enabled, client])

  return { status }
}

/**
 * Keep one entry live — `GET /logbook/{id}/stream`.
 *
 * The event carries the full `LogBookEntryOut`, so it is applied rather than
 * refetched, unless an older copy would overwrite a newer one. An event with no
 * row (the server pops its payload, so a second listener can be handed nothing)
 * falls back to a refetch. Not shared and not lingered: one open entry, one
 * socket, closed with it.
 *
 * Linked items do not push. An anomaly's status or a work order's priority
 * changing does not fire this stream — the trigger is on the logbook row alone
 * — so the briefs shown are as of the entry's last change or the last open.
 */
export function useLogbookEntryStream(id, { enabled = true } = {}) {
  const client = useQueryClient()
  const [status, setStatus] = useState('idle')

  useEffect(() => {
    if (!id || !enabled || !sseEnabled()) return undefined

    const connection = createSharedSseConnection({
      path: endpoints.logbook.detailStream(id),
      lingerMs: 0,
      onEvent: (event) => {
        if (!event?.id) {
          client.invalidateQueries({ queryKey: queryKeys.logbookEntry(id) })
        } else {
          const incoming = toEntry(event)
          client.setQueryData(queryKeys.logbookEntry(id), (previous) =>
            previous?.updatedAt && incoming.updatedAt && previous.updatedAt > incoming.updatedAt
              ? previous
              : incoming,
          )
        }
        /* The list row behind it moved too — its counts at least. */
        client.invalidateQueries({ queryKey: ['logbook', 'list'] })
      },
      onOpen: ({ reconnected }) => {
        if (reconnected) client.invalidateQueries({ queryKey: queryKeys.logbookEntry(id) })
      },
      onStatus: setStatus,
    })

    return connection.subscribe()
  }, [id, enabled, client])

  return { status }
}

/**
 * Write an entry.
 *
 * Sweeps the whole `['logbook']` root rather than one key: the entry that was
 * just written has to appear under whatever category, severity, machine and
 * date window the reader happens to have on, and `counts` moves with it.
 */
export const useCreateLogbookEntry = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createLogbookEntry,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['logbook'] }),
  })
}

/**
 * Change an entry.
 *
 * The response is the full `LogBookEntryOut`, so the detail is written from it
 * rather than refetched; the lists are swept because category, severity and
 * the counts beside them may all have moved.
 */
export const useUpdateLogbookEntry = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateLogbookEntry,
    onSuccess: (saved) => {
      queryClient.setQueryData(queryKeys.logbookEntry(saved.id), saved)
      queryClient.invalidateQueries({ queryKey: ['logbook', 'list'] })
    },
  })
}
